import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface FitSummary {
  summary: string;
  fit: number;
  strengths: string[];
  gaps: string[];
  verdict: string;
  model: string;
  created_at: number;
}

export interface SummaryResponse {
  summary: FitSummary;
  cached: boolean;
}

export interface SummaryError {
  status: number;
  error: string;
  retry_after_seconds?: number;
  detail?: string;
}

export interface GenerateSummaryInput {
  url: string;
  resume_id: string;
  force?: boolean;
}

export function useGenerateSummary() {
  const qc = useQueryClient();
  return useMutationToast<SummaryResponse, SummaryError, GenerateSummaryInput>({
    mutationFn: async (input) => {
      try {
        return await api<SummaryResponse>("/api/summaries", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toSummaryError(err);
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["summaries"] });
      qc.invalidateQueries({ queryKey: ["summary", vars.url] });
    },
    success: (data) =>
      data.cached ? "Loaded cached fit summary." : `Fit ${data.summary.fit}/10 generated.`,
    errorPrefix: "Summary failed",
  });
}

// Cached summary as a tanstack-query — replaces the prior useEffect+fetch
// drift in Jobs.tsx. Returns null when the server says there's no cache yet.
export function useCachedSummary(url: string | null) {
  return useQuery({
    queryKey: ["summary", url],
    enabled: url !== null,
    queryFn: async () => fetchCachedSummary(url!),
    staleTime: 30_000,
    retry: false,
  });
}

// Read the cached summary without invoking Anthropic. Used by useCachedSummary
// and any imperative caller that wants the same shape.
export async function fetchCachedSummary(
  url: string,
): Promise<FitSummary | null> {
  const res = await fetch(
    `/api/summaries?url=${encodeURIComponent(url)}`,
    { credentials: "same-origin" },
  );
  if (res.status === 404) return null;
  if (!res.ok) return null;
  const body = (await res.json()) as SummaryResponse;
  return body.summary;
}

function toSummaryError(err: unknown): SummaryError {
  if (err instanceof ApiError) {
    let payload: {
      error?: string;
      retry_after_seconds?: number;
      detail?: string;
    } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: payload.error ?? err.message ?? "request_failed",
      retry_after_seconds: payload.retry_after_seconds,
      detail: payload.detail,
    };
  }
  return { status: 0, error: "network_error" };
}
